import { useErrorLogger } from '@/hooks/use-error-logger.js';
import { updateInAppAssessmentResult } from '@/lib/api-calls.js';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  AssessmentPhase,
  buildInAppAssessmentResultSubmission,
  InAppAssessmentStats,
} from './in-app-assessment-utils.js';
import { StudentAssessmentState } from './use-in-app-assessment-runner.js';

/**
 * Submits the active result from the opener tree so PiP dock/pop-out can remount the panel
 * without dropping in-flight `mutate` callbacks.
 */
export function useInAppAssessmentSubmit({
  selectedAssessmentResultId,
  words,
  selectedState,
  markSubmitting,
  markSubmitted,
  revertPhase,
}: {
  selectedAssessmentResultId: string | null;
  words: string[];
  selectedState: StudentAssessmentState;
  markSubmitting: () => void;
  markSubmitted: (stats: InAppAssessmentStats) => void;
  revertPhase: (phase: AssessmentPhase) => void;
}) {
  const logError = useErrorLogger();
  const { mutate } = useMutation({
    mutationFn: updateInAppAssessmentResult,
  });

  return useCallback(
    ({ finishedEarly }: { finishedEarly: boolean }) => {
      if (!selectedAssessmentResultId) {
        return;
      }

      const data = buildInAppAssessmentResultSubmission({
        words,
        markedWordIndexes: selectedState.markedWordIndexes,
        lastWordIndex: selectedState.lastWordIndex,
        secondsRemaining: selectedState.secondsRemaining,
        finishedEarly,
        canRetell: selectedState.canRetell ?? undefined,
      });
      const stats: InAppAssessmentStats = {
        totalWordsRead: data.totalWordsRead,
        incorrectWordsCount: data.incorrectWordsCount,
        totalSecondsSpent: data.totalSecondsSpent,
        finishedEarly: data.finishedEarly,
      };

      const phaseBeforeSubmit = selectedState.phase;
      markSubmitting();

      mutate(
        {
          id: selectedAssessmentResultId,
          data,
        },
        {
          onSuccess: () => markSubmitted(stats),
          onError: (error) => {
            revertPhase(phaseBeforeSubmit);
            logError(error, { component: 'in-app-assessment', action: 'submit-result' });
          },
        },
      );
    },
    [
      logError,
      markSubmitted,
      markSubmitting,
      mutate,
      revertPhase,
      selectedAssessmentResultId,
      selectedState.canRetell,
      selectedState.lastWordIndex,
      selectedState.markedWordIndexes,
      selectedState.phase,
      selectedState.secondsRemaining,
      words,
    ],
  );
}
